import $ from 'jquery';
import PinchZoom from 'pinch-zoom-js';
import { $window, isMobile } from 'util/global';
import { appendGetParams, getParamsObject } from 'util/urls';

/**
 * Predefined zoomed image widths. This is to limit the possible versions of a given image to
 * increase the likelihood of that image already being cached at the requested size.
 * @type {Array.<Number>}
 */
const BRIOCHE_ZOOMED_IMAGE_STEPS = [
    1000,
    1500,
    2000,
    2500,
    3000,
    4000,
    5000,
];

/** @type {String} */
const CONTAINER_CLASS = 'zoomable-image';

/**
 * Determine if an image should be zoomable
 * @param {ZoomableImage} zimage
 * @returns {Boolean}
 */
function isZoomable(zimage) {
    const isZoomableImage = zimage.isBriocheImage
        || zimage.img.naturalWidth > zimage.width
        || zimage.img.naturalHeight > zimage.height;
    return isZoomableImage
        && !zimage.img.closest('.visualization')
        && (
            zimage.$img.parents('.codex-output').length === 0
            || !zimage.img.src.indexOf('.gif?t=').index < 0
        );
}

/**
 * Creates the HTML container for zoomable image and attaches the container to the zimage object
 * @param {ZoomableImage} zimage
 */
function renderZoomableImageContainer(zimage) {
    const containerHtml = `<span class="${CONTAINER_CLASS}"><span></span></span>`;
    const $container = $(containerHtml);
    const $clone = zimage.$img.clone();

    $container.append($clone);
    zimage.$img.replaceWith($container);

    zimage.$img = $clone;
    [zimage.img] = $clone;
    zimage.$container = $container;

    zimage.$container.on('click', $.proxy(zimage.show, zimage));
}

/**
 * Reattach click events for an existing zoomable image container
 * @param {ZoomableImage} zimage
 * @param {JQuery} $container
 */
function reactivateExistingImageContainer(zimage, $container) {
    zimage.$container = $container;
    zimage.$container.on('click', $.proxy(zimage.show, zimage));
}


/**
 * Get the width from the predefined width steps that's closest to screen width
 * @returns {Number}
 */
function getBriocheSteppedImageWidth() {
    const actualWidth = $window.width() * window.devicePixelRatio;
    return BRIOCHE_ZOOMED_IMAGE_STEPS.find(step => actualWidth <= step)
        || BRIOCHE_ZOOMED_IMAGE_STEPS[BRIOCHE_ZOOMED_IMAGE_STEPS.length - 1];
}

/**
 * Creates the HTML for the zoomed image modal and attaches the rendered HTML object to the
 * zimage object
 * @param {ZoomableImage} zimage
 */
function renderZoomableImageModal(zimage) {
    const $zoomModal = $(`
        <div class="modal hide zoomable-modal">
            <button></button>
            <div class="solv-ir-loading row">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>
    `);

    // Image is loading
    const loadingClass = 'loading';
    $zoomModal.addClass(loadingClass);

    // Create the new image
    const zoomedImage = new Image();
    if (zimage.isBriocheImage) {
        const [imagePath] = zimage.img.src.split('?');
        const { format } = getParamsObject(zimage.img.src);
        const zoomedParams = { width: getBriocheSteppedImageWidth() };
        if (format) {
            zoomedParams.format = format;
        }
        zoomedImage.src = appendGetParams(imagePath, zoomedParams);
    } else {
        zoomedImage.src = zimage.img.src;
    }

    // Add image to the modal
    zoomedImage.style.cssText = '';
    $zoomModal.append(zoomedImage);

    // Wait for the image to finish loading
    const $zoomedImage = $(zoomedImage);
    $zoomedImage.on('load', () => {
        $zoomModal.removeClass(loadingClass);
        if (isMobile) {
            // eslint-disable-next-line no-new
            new PinchZoom(zoomedImage);
        }
    });

    // Prevent accidentally closing the modal
    if (isMobile) {
        $zoomedImage.on('click', e => e.stopPropagation());
    }

    // Handle hide/show events
    $zoomModal.on('show hide', () => {
        setTimeout(() => {
            zimage.zoomed = !zimage.zoomed;
            $zoomModal.toggleClass('visible', zimage.zoomed);
        }, 1);
    });
    $zoomModal.modal({ show: false });
    $zoomModal.on('click', $.proxy(zimage.hide, zimage));
    zimage.$zoomModal = $zoomModal;
}

/**
 * Determines if an image needs to be ignored
 * @param {JQuery} $img
 */
function ignoreImage($img) {
    const ignoreSelectors = '.avatar, .no-zoom, .viz-wrapper';
    return $img.is(ignoreSelectors) || $img.parents(ignoreSelectors).length;
}

class ZoomableImage {
    constructor(img) {
        this.img = img;
        this.$img = $(img);
        this.zoomed = false;
        this.isBriocheImage = img.src.indexOf('/brioche/') >= 0;

        let loaded = false;
        const handleImageLoad = () => {
            if (!loaded && this.img.width) {
                loaded = true;

                const parentWidth = this.$img.parent().width();
                const width = this.$img.css('width');
                if (width.indexOf('px') >= 0) {
                    this.width = Math.min(parentWidth, parseInt(width, 10));
                } else {
                    this.width = parentWidth;
                }

                this.height = parseInt(this.$img.css('height'), 10);

                if (isZoomable(this)) {
                    const $container = this.$img.parents(`.${CONTAINER_CLASS}`);
                    if ($container.length) {
                        reactivateExistingImageContainer(this, $container);
                    } else {
                        renderZoomableImageContainer(this);
                    }
                }
            }
        };

        // try to make the image zoomable right away...
        setTimeout(handleImageLoad, 1);

        // ... but it might not be ready yet, so wait until it's loaded
        this.$img.on('load', handleImageLoad);
    }

    /** Show the zoomed image modal */
    show() {
        if (!Object.prototype.hasOwnProperty.call(this, '$zoomModal')) {
            renderZoomableImageModal(this);
        }
        this.$zoomModal.modal('show');
    }

    /** Hide the zoomed image modal */
    hide() {
        this.$zoomModal.modal('hide');
    }
}

/**
 * Find any possible zoomable images within the container and make them zoomable
 * @param {JQuery} $container
 */
export function images($container) {
    $container.find('img').each((_, img) => {
        const $img = $(img);
        if (!ignoreImage($img)) {
            const zoomableInit = () => new ZoomableImage(img);
            if (img.complete) {
                zoomableInit();
            } else {
                $img.on('load', zoomableInit);
            }
        }
    });
}
